/** Shared parsing for AI capsule responses (Results + Market Analysis recap). */

function parseBoldLeadBlocks(md) {
  if (!md?.trim()) return [];
  const blocks = [];
  const re = /\*\*([^*]+)\*\*\s*([\s\S]*?)(?=\n*\*\*|$)/g;
  let m;
  while ((m = re.exec(md)) !== null) {
    const title = m[1].trim();
    const body = m[2].trim();
    if (title.length && title.length < 100) blocks.push({ title, body });
  }
  return blocks;
}

function parseListColonBlocks(text) {
  if (!text?.trim()) return [];
  const blocks = [];
  const seen = new Set();
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const stripped = line
      .replace(/^\s*(?:[-*•]|\d+[.)]+)\s+/, "")
      .replace(/\*\*/g, "")
      .trim();
    const idx = stripped.indexOf(":");
    if (idx <= 1 || idx >= stripped.length - 1) continue;
    const title = stripped.slice(0, idx).trim();
    const body = stripped.slice(idx + 1).trim();
    if (title.length < 2 || body.length < 1) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    blocks.push({ title, body });
  }
  return blocks;
}

function extractCompanionPreamble(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const firstBullet = lines.findIndex((l) => l && /^\s*(?:[-*•]|\d+[.)]+)\s+/.test(l));
  if (firstBullet <= 0) return null;
  const before = lines.slice(0, firstBullet).filter(Boolean);
  if (before.length >= 2) {
    return { title: before[0], body: before.slice(1).join(" ") };
  }
  if (before.length === 1) {
    return { title: before[0], body: "" };
  }
  return null;
}

export function normalizeListMarkdownToParagraphs(text) {
  if (!text?.trim()) return text;
  const lines = text.split(/\r?\n/);
  const chunks = [];
  let buf = [];
  const flushBuf = () => {
    if (buf.length) {
      chunks.push(buf.join(" "));
      buf = [];
    }
  };
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushBuf();
      continue;
    }
    const isList = /^\s*(?:[-*•]|\d+[.)]+)\s+/.test(line);
    const content = trimmed.replace(/^\s*(?:[-*•]|\d+[.)]+)\s+/, "").trim();
    if (isList) {
      flushBuf();
      chunks.push(content);
    } else {
      buf.push(trimmed);
    }
  }
  flushBuf();
  return chunks.join("\n\n").replace(/\n{3,}/g, "\n\n").trim();
}

function mergeBlocksUnique(blocks) {
  const m = new Map();
  for (const b of blocks) {
    const k = b.title.toLowerCase();
    if (!m.has(k)) m.set(k, b);
  }
  return Array.from(m.values());
}

function tokenizeForTitleMatch(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3);
}

/** "Fabric A (140 GSM)Fabric B (130 GSM)" → separate material titles. */
function splitMergedGsmTitleParts(title) {
  const t = (title || "").replace(/\*\*/g, "").trim();
  if (!t) return [];

  const gsmMatches = [...t.matchAll(/\(\s*\d{2,4}\s*gsm\s*\)/gi)];
  if (gsmMatches.length < 2) return [t];

  const parts = [];
  let lastStart = 0;
  for (const match of gsmMatches) {
    const gsmEnd = match.index + match[0].length;
    const part = t.slice(lastStart, gsmEnd).trim();
    if (part) parts.push(part);
    lastStart = gsmEnd;
  }
  return parts.length >= 2 ? parts : [t];
}

/** Pick the title segment that best matches the paragraph below it. */
function pickTitleMatchingBody(title, body) {
  const materialParts = splitMergedGsmTitleParts(title);
  if (materialParts.length > 1) {
    const bodyLower = (body || "").toLowerCase();
    let best = materialParts[materialParts.length - 1];
    let bestScore = -1;
    for (const part of materialParts) {
      const name = part.replace(/\(\s*\d{2,4}\s*gsm\s*\)/i, "").trim();
      const score = tokenizeForTitleMatch(name).filter((w) => bodyLower.includes(w)).length;
      if (score > bestScore) {
        bestScore = score;
        best = part;
      }
    }
    return bestScore > 0 ? best : materialParts[materialParts.length - 1];
  }

  const companionParts = title
    .split(/(?<=[a-z])(?=[A-Z])/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (companionParts.length > 1) {
    const bodyLower = (body || "").toLowerCase();
    let best = companionParts[companionParts.length - 1];
    let bestScore = -1;
    for (const part of companionParts) {
      const score = tokenizeForTitleMatch(part).filter((w) => bodyLower.includes(w)).length;
      if (score > bestScore) {
        bestScore = score;
        best = part;
      }
    }
    return bestScore > 0 ? best : companionParts[companionParts.length - 1];
  }

  return title;
}

function normalizeDisplayBlocks(blocks) {
  return (blocks || []).map((block) => {
    if (!block?.title) return block;
    const title = pickTitleMatchingBody(block.title, block.body || "");
    return title === block.title ? block : { ...block, title };
  });
}

function splitOnMultipleGsmSpecs(text) {
  const t = (text || "")
    .replace(/\*\*/g, "")
    .replace(/\uFEFF|[\u200B-\u200D\u2060]/g, "")
    .trim();
  if (!t) return [];

  const gsmRe =
    /\b([A-Za-z0-9][A-Za-z0-9%/,.\-'’\s]{0,86}?\(\s*\d{2,4}\s*gsm\s*\))/gi;
  const matches = [...t.matchAll(gsmRe)];
  if (matches.length < 2) return [];

  const blocks = [];
  for (let i = 0; i < matches.length; i++) {
    const title = matches[i][1].replace(/\s+/g, " ").trim();
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : t.length;
    let body = t.slice(start, end).trim().replace(/\s+/g, " ");
    body = body
      .replace(/^[:;\u2014\u2013\-–\s]+/, "")
      .replace(/\*\*\s*\d{1,2}\s*\*\*/g, "")
      .replace(/\[\d+\]/g, "")
      .trim();
    if (title.length >= 3 && body.length >= 8) blocks.push({ title, body });
  }

  if (matches[0].index > 15) {
    const lead = t.slice(0, matches[0].index).trim();
    if (lead && blocks[0]) blocks[0].body = `${lead} ${blocks[0].body}`.trim();
  }

  return blocks.length >= 2 ? normalizeDisplayBlocks(blocks) : [];
}

/**
 * "Organic Cotton (180 GSM): blend... Brushed Fleece (240 GSM): ..." → titled blocks.
 * Also accepts en dash/em dash after the weight: "... (220 GSM) — text".
 */
export function splitInlineFabricSpecBlocks(text) {
  const t = (text || "")
    .replace(/\*\*/g, "")
    .replace(/\uFEFF|[\u200B-\u200D\u2060]/g, "")
    .trim();
  if (!t) return [];

  const fromGsmHeadings = splitOnMultipleGsmSpecs(t);
  if (fromGsmHeadings.length >= 2) return fromGsmHeadings;

  const headRe =
    /\b(([A-Za-z0-9][A-Za-z0-9%/,.\-'’\s]{0,86}?)\(\s*\d{2,4}\s*gsm\s*\))\s*[:\u2014\u2013\-–]\s*/gi;

  const matches = [...t.matchAll(headRe)];
  if (!matches.length) return [];

  const blocks = [];
  for (let i = 0; i < matches.length; i++) {
    const title = matches[i][1].replace(/\s+/g, " ").trim();
    const start = matches[i].index + matches[i][0].length;
    const end =
      i + 1 < matches.length ? matches[i + 1].index : t.length;
    let body = t.slice(start, end).trim().replace(/\s+/g, " ");
    body = body
      .replace(/\*\*\s*\d{1,2}\s*\*\*/g, "")
      .replace(/\[\d+\]/g, "")
      .trim();
    if (title.length >= 3 && body.length >= 8) blocks.push({ title, body });
  }

  if (matches[0].index > 15) {
    const lead = t.slice(0, matches[0].index).trim();
    if (lead && blocks[0]) blocks[0].body = `${lead} ${blocks[0].body}`.trim();
  }

  const normalized =
    blocks.length >= 2 ||
    (blocks.length === 1 && blocks[0].body.length >= 100)
      ? normalizeDisplayBlocks(blocks)
      : [];

  return normalized;
}

export function parseMaterialsForDisplay(md) {
  if (!md?.trim()) return { mode: "markdown", text: md || "" };
  const fromBold = parseBoldLeadBlocks(md);
  const fromList = parseListColonBlocks(md);
  let merged = mergeBlocksUnique([...fromBold, ...fromList]);

  if (merged.length === 1) {
    const b = merged[0];
    let chunk = (b.body || "").trim();
    if (/^materials$/i.test(b.title.trim())) {
      chunk = chunk || md.replace(/\*\*/g, "").trim();
    }
    chunk = stripOuterMaterialsHeading(
      chunk.replace(/^\*\*materials\*\*:?\s*/i, "")
    );

    let sliced = splitInlineFabricSpecBlocks(chunk);
    if (
      sliced.length < 2 &&
      !/^materials$/i.test(b.title.trim()) &&
      `${b.title} ${b.body || ""}`.trim().length > 80
    ) {
      sliced = splitInlineFabricSpecBlocks(
        stripOuterMaterialsHeading(`${b.title}: ${b.body || ""}`.trim())
      );
    }
    if (sliced.length >= 2) merged = sliced;
    else if (sliced.length === 1 && sliced[0].body.length >= 90) merged = sliced;
  }

  if (!merged.length) {
    const flat = stripOuterMaterialsHeading(md.replace(/\*\*/g, "").trim());
    let sliced = splitInlineFabricSpecBlocks(flat);
    if (sliced.length >= 2) merged = sliced;
    else if (sliced.length === 1 && sliced[0].body.length >= 90) merged = sliced;
  }

  if (merged.length >= 1) {
    return { mode: "blocks", blocks: normalizeDisplayBlocks(merged) };
  }

  const colonBlocks = [];
  for (const para of md.split(/\n\n+/)) {
    const line = para.replace(/\*\*/g, "").split("\n")[0]?.trim();
    if (!line) continue;
    const cm =
      line.match(/^([^:–—-]{2,80})[:–—-]\s*(.+)$/) ||
      line.match(/^([^:–—-]{2,80}):\s*(.+)$/);
    if (cm) {
      colonBlocks.push({
        title: cm[1].replace(/^[-*•\s]+/, "").trim(),
        body: (cm[2] + para.slice(line.length)).trim(),
      });
    }
  }
  if (colonBlocks.length >= 1) {
    return { mode: "blocks", blocks: normalizeDisplayBlocks(colonBlocks) };
  }
  return {
    mode: "markdown",
    text: normalizeListMarkdownToParagraphs(md),
  };
}

function stripOuterMaterialsHeading(s) {
  return s.replace(/^\s*materials\s*:?\s*/i, "").trim();
}

export function parseSalesPriceForDisplay(text) {
  if (!text?.trim()) return { body: "", retailValue: null };

  let retailValue = null;
  const rangePatterns = [
    /(?:Recommended|Suggested)\s+retail\s*:?\s*\*?\s*(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)/i,
    /\*\*Retail\s+price:\*\*\s*(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)/i,
    /(?:Retail\s+price|RRP|MSRP)\s*:?\s*(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)/i,
    /retail\s+(?:range|of|at|around|is)\s*:?\s*(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)/i,
    /(?:^|\n)\s*(?:Recommended\s+retail|Retail\s+price|Retail)\s*:?\s*(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)/im,
  ];
  for (const re of rangePatterns) {
    const m = text.match(re);
    if (m?.[1]) {
      retailValue = m[1].replace(/\s*-\s*/g, "–");
      break;
    }
  }
  if (!retailValue) {
    const pair = text.match(/(\$[\d,.]+)\s*[-–]\s*(\$[\d,.]+)/);
    if (pair) retailValue = `${pair[1]}–${pair[2]}`;
  }
  if (!retailValue) {
    const all = text.match(/\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?/g);
    if (all?.length) retailValue = all[all.length - 1];
  }

  let body = text;
  if (retailValue) {
    const esc = retailValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    body = body
      .replace(
        new RegExp(
          `(?:\\*\\*)?(?:Recommended|Suggested)\\s+retail\\s*:?\\s*(?:\\*\\*)?\\s*${esc}[^\\n]*`,
          "gi"
        ),
        ""
      )
      .replace(
        new RegExp(`\\*\\*Retail\\s+price:\\*\\*\\s*${esc}[^\\n]*`, "gi"),
        ""
      )
      .replace(
        new RegExp(`(?:Retail\\s+price|RRP|MSRP)\\s*:?\\s*${esc}[^\\n]*`, "gi"),
        ""
      )
      .replace(
        new RegExp(`^\\s*[-*•]?\\s*Recommended\\s+retail[^\\n]*${esc}[^\\n]*$`, "gim"),
        ""
      )
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  body = normalizeListMarkdownToParagraphs(body);
  body = body
    .replace(
      /^\*\*(?:Recommended\s+retail|Retail\s+price):\*\*\s*\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?\s*/im,
      ""
    )
    .replace(
      /^(?:Recommended\s+retail|Retail\s+price):\s*\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?\s*/im,
      ""
    )
    .trim();

  return { body: body || text, retailValue };
}

export function parseCompanionForDisplay(text) {
  if (!text?.trim()) return { mode: "markdown", text: text || "" };

  const preamble = extractCompanionPreamble(text);
  const listBlocks = parseListColonBlocks(text);
  const fromBold = parseBoldLeadBlocks(text);

  const mergedPieces = mergeBlocksUnique([...listBlocks, ...fromBold]);
  const preambleTitle = (preamble?.title || "").toLowerCase();

  const blocks = [];
  if (preamble?.title) blocks.push(preamble);
  for (const b of mergedPieces) {
    if (
      preambleTitle &&
      b.title.toLowerCase() === preambleTitle &&
      !b.body.trim()
    )
      continue;
    blocks.push(b);
  }

  if (blocks.length >= 1) {
    return { mode: "blocks", blocks: normalizeDisplayBlocks(blocks) };
  }

  const out = [];
  for (const block of text.split(/\n\n+/)) {
    const raw = block.trim();
    if (!raw) continue;
    const firstLine = raw.split(/\r?\n/)[0];
    const plain = firstLine.replace(/\*\*/g, "");
    const mc = plain.match(/^([^:–—]{2,100})[:–—]\s*(.*)$/);
    if (mc) {
      const restOfBlock = raw.slice(firstLine.length).trim();
      const bodyPart = mc[2].trim()
        ? `${mc[2].trim()}${restOfBlock ? `\n${restOfBlock}` : ""}`
        : restOfBlock;
      out.push({
        title: mc[1].replace(/^[-*•\d.]+\s*/, "").trim(),
        body: bodyPart || mc[2].trim(),
      });
    }
  }
  if (out.length > 0) {
    return { mode: "blocks", blocks: normalizeDisplayBlocks(out) };
  }
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const plain = line.replace(/\*\*/g, "");
    const mc = plain.match(/^([^:–—]{2,100})[:–—]\s*(.+)$/);
    if (mc) {
      out.push({
        title: mc[1].replace(/^[-*•\d.]+\s*/, "").trim(),
        body: mc[2].trim(),
      });
    }
  }
  if (out.length > 0) {
    return { mode: "blocks", blocks: normalizeDisplayBlocks(out) };
  }
  return {
    mode: "markdown",
    text: normalizeListMarkdownToParagraphs(text),
  };
}

/** Prompt order — slice content until the next **Heading** (avoids truncating on in-section **bold** lines). */
const CAPSULE_SECTION_ORDER_KEYS = [
  "Materials",
  "Sales Price",
  "Color Palette",
  "Cost Production",
  "Companion Items",
  "Yield & Consumption Estimates",
  "Production Lead Time Estimate",
  "Market & Brand Positioning",
  "Comparable Market Examples",
  "Target Consumer Insight",
  "Business & Financial Tools",
  "Margin Analysis",
  "Wholesale vs. DTC Pricing",
];

function escapeCapsuleRe(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function followingCapsuleSectionTitles(orderKey) {
  const i = CAPSULE_SECTION_ORDER_KEYS.indexOf(orderKey);
  if (i < 0) return CAPSULE_SECTION_ORDER_KEYS.slice(1);
  const tail = CAPSULE_SECTION_ORDER_KEYS.slice(i + 1);
  const out = [...tail];
  if (orderKey === "Margin Analysis") {
    for (const h of [
      "Wholesale vs. DTC Pricing",
      "Wholesale vs DTC Pricing",
      "Wholesale vs DTC",
    ]) {
      if (!out.includes(h)) out.push(h);
    }
  }
  return out;
}

/**
 * Extract one capsule section from raw AI markdown using the next major **Heading** as the end boundary.
 * @param {string} text full response (after prompt strip)
 * @param {string|string[]} startLabels **Label** variants to locate the section start
 * @param {string} boundaryOrderKey key from CAPSULE_SECTION_ORDER_KEYS for "following" boundaries
 */
export function extractCapsuleSection(text, startLabels, boundaryOrderKey) {
  if (!text?.trim()) return "";
  const variants = Array.isArray(startLabels) ? startLabels : [startLabels];
  let bestIdx = -1;
  let headerLen = 0;
  for (const lab of variants) {
    const esc = escapeCapsuleRe(lab);
    const re = new RegExp(`(?:^|\\n)\\s*(?:#{1,6}\\s+)?(?:\\*\\*)?${esc}(?:\\*\\*)?\\s*:?\\s*`, "i");
    const m = text.match(re);
    if (m && typeof m.index === "number" && (bestIdx < 0 || m.index < bestIdx)) {
      bestIdx = m.index;
      headerLen = m[0].length;
    }
  }
  if (bestIdx < 0) return "";
  const bodyStart = bestIdx + headerLen;
  const following = followingCapsuleSectionTitles(boundaryOrderKey);
  let end = text.length;
  if (following.length) {
    const inner = following.map((h) => escapeCapsuleRe(h)).join("|");
    const endRe = new RegExp(`(?:^|\\n)\\s*(?:#{1,6}\\s+)?(?:\\*\\*)?(?:${inner})(?:\\*\\*)?\\s*:?\\s*(?:\\n|$)`, "i");
    const sub = text.slice(bodyStart);
    const hit = sub.search(endRe);
    if (hit >= 0) end = bodyStart + hit;
  }
  return text.slice(bodyStart, end).trim();
}

/** Split comparable lines into brand tokens (handles one line "A, B, C, D"). */
export function expandComparableMarketItems(text) {
  const t = (text || "").replace(/\*\*/g, "").trim();
  if (!t) return [];
  const lines = t
    .split(/\r?\n/)
    .map((l) => l.replace(/^[-*•\d.]+\s*/, "").trim())
    .filter(Boolean);
  const items = [];
  for (const line of lines) {
    if (/,|•|\/|;/.test(line)) {
      for (const p of line.split(/(?:,|•|\/|;)\s*/)) {
        const s = p.trim();
        if (s) items.push(s);
      }
    } else if (line) {
      items.push(line);
    }
  }
  const dedup = [];
  const seen = new Set();
  for (const it of items) {
    const k = it.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    dedup.push(it);
  }
  return dedup;
}

export function countComparableMarketExamples(text) {
  return expandComparableMarketItems(text).length;
}

/** Prompt example palette — if all four appear, model likely copied the template. */
export const CAPSULE_EXAMPLE_PALETTE_HEX = [
  "#0066FF",
  "#FF6347",
  "#228B22",
  "#36454F",
];

export function colorsLookLikePromptExamples(text) {
  const upper = (text || "").toUpperCase();
  if (!upper.trim()) return false;
  return CAPSULE_EXAMPLE_PALETTE_HEX.every((h) => upper.includes(h));
}

const PRICING_START_LABELS = [
  "Wholesale vs. DTC Pricing",
  "Wholesale vs DTC Pricing",
  "Wholesale vs DTC",
  "Wholesale vs Direct-to-Consumer Pricing",
  "Wholesale vs. Direct-to-Consumer (DTC) Pricing",
  "Wholesale and DTC Pricing",
];

const MARGIN_START_LABELS = ["Margin Analysis"];

/** Re-extract financial blocks from raw markdown when parsed keys are empty. */
export function repairFinancialSections(parsed, rawText) {
  const out = parsed && typeof parsed === "object" ? { ...parsed } : {};
  const raw = (rawText || "").trim();
  if (!raw) return out;

  if (!out.marginAnalysis?.trim()) {
    out.marginAnalysis = extractCapsuleSection(
      raw,
      MARGIN_START_LABELS,
      "Margin Analysis"
    );
  }
  if (!out.pricing?.trim()) {
    out.pricing = extractCapsuleSection(
      raw,
      PRICING_START_LABELS,
      "Wholesale vs. DTC Pricing"
    );
  }
  if (!out.pricing?.trim()) {
    const tail = raw.match(
      /(?:^|\n)\s*Wholesale\s+price\s+range\s*:\s*[\s\S]*$/im
    );
    if (tail?.[0]?.trim()) {
      out.pricing = tail[0].trim();
    }
  }
  if (!out.marginAnalysis?.trim()) {
    const tail = raw.match(
      /(?:^|\n)\s*Retail\s+price\s*:\s*\$[\s\S]*?(?=\n\s*\*\*Wholesale|\n\s*Wholesale\s+price\s+range\s*:|$)/im
    );
    if (tail?.[0]?.trim()) {
      out.marginAnalysis = tail[0].trim();
    }
  }
  return out;
}

/** Fill missing parsed keys from raw answer (navigation cache / partial parses). */
export function repairParsedCapsule(parsed, rawText) {
  let out = repairFinancialSections(parsed, rawText);
  const raw = (rawText || "").trim();
  if (!raw) return out;

  const fill = (key, labels, boundary) => {
    if (out[key]?.trim()) return;
    const slice = extractCapsuleSection(raw, labels, boundary);
    if (slice.trim()) out[key] = slice;
  };

  fill("materials", ["Materials"], "Materials");
  fill("companionItems", ["Companion Items"], "Companion Items");
  fill(
    "marketExamples",
    ["Comparable Market Examples"],
    "Comparable Market Examples"
  );
  fill("colors", ["Color Palette", "Color Palette with HEX Codes", "Colours", "Colors"], "Color Palette");
  fill(
    "yieldConsumption",
    [
      "Yield & Consumption Estimates",
      "Yield and Consumption Estimates",
      "Yield & Consumption",
      "Yield and Consumption",
      "Fabric Yield & Consumption Estimates",
      "Fabric Yield and Consumption Estimates",
    ],
    "Yield & Consumption Estimates"
  );
  fill(
    "targetInsight",
    [
      "Target Consumer Insight",
      "Target Consumer Insights",
      "Target Consumer",
      "Target Audience",
      "Target Audience Insight",
      "Target Audience Insights",
      "Consumer Insight",
      "Consumer Insights",
      "Target Consumer Profile",
      "Target Consumer Profiles",
    ],
    "Target Consumer Insight"
  );
  fill(
    "leadTime",
    ["Production Lead Time Estimate", "Production Lead Time", "Lead Time Estimate", "Lead Time"],
    "Production Lead Time Estimate"
  );
  return out;
}
